import { useCallback, useEffect, useRef } from "react";

const AudioVisualizer = ({ mediaUrl, isPlaying, analyser: externalAnalyser }) => {
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const audioContextRef = useRef(null);
  const mediaElementRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserRef = useRef(null);

  const stopAnimation = useCallback(() => {
    if (!animationFrameId.current) return;
    cancelAnimationFrame(animationFrameId.current);
    animationFrameId.current = null;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const drawIdleLine = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = "rgba(249, 115, 22, 0.18)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawIdleLine();
  }, [drawIdleLine]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;

    if (!canvas || !analyser) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    stopAnimation();

    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.8;

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const render = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      analyser.getByteTimeDomainData(dataArray);
      ctx.clearRect(0, 0, width, height);

      /* CENTER GUIDELINE */

      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.strokeStyle = "rgba(249, 115, 22, 0.08)";
      ctx.lineWidth = 1;
      ctx.stroke();

      /* WAVEFORM */

      ctx.beginPath();

      const sliceWidth = width / (bufferLength - 1);

      for (let i = 0; i < bufferLength; i++) {
        const normalized = dataArray[i] / 128;
        const y = (normalized * height) / 2;
        const x = i * sliceWidth;

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }

      ctx.strokeStyle = "rgb(249, 115, 22)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();

      animationFrameId.current = requestAnimationFrame(render);
    };

    render();
  }, [stopAnimation]);

  /* CANVAS RESIZE */

  useEffect(() => {
    resizeCanvas();

    const handleResize = () => resizeCanvas();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resizeCanvas]);

  /* AUDIO / ANALYSER */

  useEffect(() => {
    let cancelled = false;

    const cleanupInternalAudio = async () => {
      stopAnimation();

      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.disconnect();
        } catch {
          // Already disconnected
        }

        sourceNodeRef.current = null;
      }

      if (analyserRef.current && !externalAnalyser) {
        try {
          analyserRef.current.disconnect();
        } catch {
          // Already disconnected
        }

        analyserRef.current = null;
      }

      if (mediaElementRef.current) {
        mediaElementRef.current.pause();
        mediaElementRef.current.src = "";
        mediaElementRef.current = null;
      }

      if (audioContextRef.current && !externalAnalyser) {
        try {
          await audioContextRef.current.close();
        } catch {
          // Context may already be closed
        }

        audioContextRef.current = null;
      }
    };

    /* RECORDING MODE */

    if (externalAnalyser) {
      analyserRef.current = externalAnalyser;

      if (isPlaying) draw();
      else {
        stopAnimation();
        drawIdleLine();
      }

      return () => {
        stopAnimation();
      };
    }

    /* PLAYBACK MODE */

    if (mediaUrl && isPlaying) {
      const setupAudio = async () => {
        try {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (!AudioContextClass) return;

          const audioContext = new AudioContextClass();
          audioContextRef.current = audioContext;

          if (audioContext.state === "suspended") await audioContext.resume();

          if (cancelled) {
            await audioContext.close();
            return;
          }

          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 1024;
          analyser.smoothingTimeConstant = 0.8;

          const mediaElement = new Audio(mediaUrl);
          mediaElement.crossOrigin = "anonymous";

          const sourceNode = audioContext.createMediaElementSource(mediaElement);

          sourceNode.connect(analyser);
          analyser.connect(audioContext.destination);

          analyserRef.current = analyser;
          mediaElementRef.current = mediaElement;
          sourceNodeRef.current = sourceNode;

          mediaElement.addEventListener("ended", () => {
            stopAnimation();
            drawIdleLine();
          });

          await mediaElement.play();

          if (!cancelled) draw();
        } catch (error) {
          console.error("Error setting up audio visualizer:", error);
        }
      };

      setupAudio();
    } else {
      stopAnimation();

      if (mediaElementRef.current) mediaElementRef.current.pause();

      drawIdleLine();
    }

    return () => {
      cancelled = true;
      cleanupInternalAudio();
    };
  }, [mediaUrl, isPlaying, externalAnalyser, draw, stopAnimation, drawIdleLine]);

  /* UNMOUNT */

  useEffect(() => {
    return () => {
      clearCanvas();
      stopAnimation();
    };
  }, [clearCanvas, stopAnimation]);

  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-orange-100 bg-gradient-to-r from-orange-50/70 via-white to-orange-50/50 px-3 py-2">
      {/* RECORDING PULSE */}

      {isPlaying && <div className="absolute left-3 top-1/2 z-10 h-2 w-2 -translate-y-1/2 animate-pulse rounded-full bg-orange-500" />}

      {/* WAVEFORM */}

      <canvas ref={canvasRef} className="block h-12 w-full" />

      {/* EDGE FADES */}

      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-orange-50/60 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-orange-50/50 to-transparent" />
    </div>
  );
};

export default AudioVisualizer;